import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { getTickets, getTicket, replyToTicket } from '../api/support';
import { useToast } from '../context/ToastContext';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Drawer from '../components/ui/Drawer';
import Pagination from '../components/ui/Pagination';
import SearchInput from '../components/ui/SearchInput';
import { Skeleton } from '../components/ui/Skeleton';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { formatDate, getStatusColor } from '../lib/utils';
import clsx from 'clsx';

const PAGE_SIZE = 25;
const STATUS_FILTERS = ['all', 'open', 'in_progress', 'resolved', 'closed'];

function TicketThread({ ticketId }) {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [replyText, setReplyText] = useState('');
  const bottomRef = useRef(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => getTicket(ticketId).then((r) => r.data),
    enabled: !!ticketId,
    // Polls instead of waiting on a WS push — this is an actively-open
    // conversation view, so a short interval keeps it feeling live without
    // needing a persistent socket.
    refetchInterval: 4000,
  });

  // The backend marks the ticket read as a side effect of this same fetch —
  // refresh the list/badge right away instead of waiting for their own
  // polling intervals to catch up.
  useEffect(() => {
    if (ticket) {
      qc.invalidateQueries({ queryKey: ['support-tickets'] });
      qc.invalidateQueries({ queryKey: ['support', 'unread-count'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  const reply = useMutation({
    mutationFn: (text) => replyToTicket(ticketId, { text }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      setReplyText('');
    },
    onError: () => addToast('Failed to send reply', 'error'),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  if (isLoading) {
    return <div className="px-6 py-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>;
  }

  if (!ticket) return null;

  const messages = ticket.messages ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 space-y-1">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ticket.subject}</p>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusColor(ticket.status)}>{ticket.status}</Badge>
          {ticket.category && <Badge variant="default">{ticket.category}</Badge>}
        </div>
        {ticket.ride_id && <p className="text-xs text-gray-400">Ride #{ticket.ride_id}</p>}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
        {messages.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">No messages yet</p>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={clsx('flex', msg.is_agent ? 'justify-end' : 'justify-start')}>
              <div
                className={clsx(
                  'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm',
                  msg.is_agent
                    ? 'bg-amber-500 text-gray-900 rounded-br-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-bl-sm'
                )}
              >
                <p>{msg.text}</p>
                <p className={clsx('text-xs mt-1 opacity-70', msg.is_agent ? 'text-right' : '')}>{formatDate(msg.created_at)}</p>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      <div
        className="shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <form
          onSubmit={(e) => { e.preventDefault(); if (replyText.trim()) reply.mutate(replyText.trim()); }}
          className="flex gap-2"
        >
          <input
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type a reply…"
            className="flex-1 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 placeholder-gray-400"
          />
          <Button type="submit" icon={Send} loading={reply.isPending} disabled={!replyText.trim()}>
            Send
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function Support() {
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim());

  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets', page, statusFilter, debouncedSearch],
    queryFn: () =>
      getTickets({
        page,
        limit: PAGE_SIZE,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      }).then((r) => ({
        items: r.data,
        total: Number(r.headers['x-total-count'] ?? r.data.length),
      })),
    placeholderData: keepPreviousData,
    // Keeps status/ordering current as new tickets/messages come in,
    // without needing a WS push.
    refetchInterval: 10000,
  });

  const tickets = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns = [
    { key: 'id', label: 'ID', render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    { key: 'user_id', label: 'User', render: (v, row) => <span className="text-xs text-gray-600 dark:text-gray-300">{row.user_name ?? `#${v}`}</span> },
    {
      key: 'subject', label: 'Subject',
      render: (v, row) => (
        <div className="flex items-center gap-2 max-w-[220px]">
          {row.unread_count > 0 && (
            <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />
          )}
          <span className={clsx('truncate', row.unread_count > 0 ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-900 dark:text-gray-100')}>
            {v ?? '—'}
          </span>
          {row.unread_count > 0 && (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white leading-none shrink-0">
              {row.unread_count > 9 ? '9+' : row.unread_count}
            </span>
          )}
        </div>
      ),
    },
    { key: 'category', label: 'Category', render: (v) => v ? <Badge variant="info">{v}</Badge> : '—' },
    {
      key: 'status', label: 'Status',
      render: (v) => <Badge variant={getStatusColor(v)}>{v ?? '—'}</Badge>,
    },
    { key: 'ride_id', label: 'Ride', render: (v) => v ? <span className="font-mono text-xs text-gray-500">#{v}</span> : '—' },
    { key: 'created_at', label: 'Created', render: (v) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span> },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Support Tickets</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{total} tickets</p>
        </div>
        <SearchInput
          placeholder="Search subject, user, ticket or ride ID…"
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
          className="w-full sm:w-80"
        />
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={clsx(
              'px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors',
              statusFilter === s
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <Table
        columns={columns}
        data={tickets}
        loading={isLoading}
        onRowClick={(row) => setSelectedTicketId(row.id)}
        emptyMessage={debouncedSearch ? `No tickets match “${debouncedSearch}”` : 'No support tickets'}
      />
      {!isLoading && tickets.length > 0 && (
        <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}

      <Drawer
        isOpen={!!selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        title={`Ticket #${selectedTicketId}`}
      >
        {selectedTicketId && <TicketThread ticketId={selectedTicketId} />}
      </Drawer>
    </div>
  );
}
